import _ from 'lodash';
import { defineStore, storeToRefs } from 'pinia';
import { set } from 'vue-demi';

import { ISessionEvent } from '@/types';
import { AppRouter, notifyInBrowser } from '@/common';
import { useEventTemplates } from '../event-templates';
import { useNewEvents } from '../new-events';
import { useHelpers } from '@/composables';
import { useSessionsStore } from '@/modules/sessions-store';
import { useContacts } from '@/modules/contacts-store';
import { HITL, Ringtone } from '@/api';
import { UIEventBus, THROW_API_ERROR } from '@/common/ui-event-bus';
import { SESSION_STATUS } from '@/constants';

export const useEventsStore = defineStore('HITL-Events', {
  state: () => {
    return {
      eventsBySessionId: {} as { [eventId: string]: ISessionEvent[]; },
    };
  },
  getters: {
    getEventsBySessionId: state => {
      return (sessionId: string) => {
        return state.eventsBySessionId[sessionId] || [];
      };
    },
  },
});

export const useEventsActions = () => {
  const store = useEventsStore();
  const { getSessionById, updateEventTimestamp } = useSessionsStore();
  const { getContactById, updateContactEventTimestamp } = useContacts();

  const addEventsToStore = (args: {
    sessionId: string;
    events: ISessionEvent[];
  }) => {
    const current = store.eventsBySessionId[args.sessionId] || [];
    const update = _.unionBy(current.concat(args.events), 'eventId');
    const eventsOrderByTimestamp = _.sortBy(update, e => {
      return new Date(e.timestamp).getTime();
    });

    set(store.eventsBySessionId, args.sessionId, eventsOrderByTimestamp);
  };

  /** TODO: Add an option to return events without saving in store */
  const getAPIEventsBySessionId = async (args: { sessionId: string; }) => {
    try {
      const { sessionId } = args;
      const response = await HITL.sessionEvents
        .getSessionEventsList({ sessionId });
      const events = response.data as any as ISessionEvent[];

      return events && events.length
        ? processEvents({ sessionId, events })
        : [] as ISessionEvent[];
    } catch (error) {
      UIEventBus.$emit(THROW_API_ERROR, { error });
      throw error;
    }
  };

  /** Manually create an event and add to the database */
  const createUIEvent = async (args: { sessionId: string; label: string; }) => {
    const EVENT_TYPE = 'Step';
    const EVENT_CATEGORY = 'UI';

    const { sessionId, label } = args;
    const events = [
      {
        event: EVENT_TYPE,
        eventCategory: EVENT_CATEGORY,
        timestamp: new Date().toISOString(),
        eventValue: { StepLabel: label },
      },
    ] as any[];

    try {
      await HITL.sessionEvents.addEvents({ sessionId, events });
      await processEvents({ sessionId, events });
    } catch (error) {
      UIEventBus.$emit(THROW_API_ERROR, { error });
    }
  };

  /** Receive events from WebSocket and process them */
  const wsAddEvents = async (args: {
    sessionId: string;
    events: ISessionEvent[];
  }) => {
    /* TODO: Use new sessions store! */
    const { sessionId, events } = args;
    const { sessionId: activeSessionId } = AppRouter.getQuery();

    /* If contact is not visible in the contact list - do not play notification sounds */
    const session = getSessionById.value(sessionId);
    const playSoundOnEvent = !!getContactById.value(session.contactId);

    await processEvents({
      sessionId,
      events,
      playSoundOnEvent,
      updateReadStatus: true,
      markAsRead: sessionId === activeSessionId,
    });
  };

  /** TODO: Split in basic and advanced process methods! */
  const processEvents = async (args: {
    sessionId: string;
    events: ISessionEvent[];
    playSoundOnEvent?: boolean;
    updateReadStatus?: boolean;
    markAsRead?: boolean;
  }) => {
    const { sessionId } = args;
    const { getEventTemplate } = useEventTemplates();
    const { addToNewEventsCount, saveReadTextEvent } = useNewEvents();
    const { userId: agentId } = useHelpers();

    let playSoundOnEvent = args.playSoundOnEvent || false;
    let updateReadStatus = args.updateReadStatus || false;

    const events = args.events
      .map(event => {
        return event.is ? event : { ...event, ...getEventTemplate(event) };
      })
      .filter(event => {
        return !event.templateId || event.templateId && event.visibility;
      }) as ISessionEvent[];

    addEventsToStore({ sessionId, events });

    /* We aren't interested in additional actions for sessions that are claimed by other agents */
    const session = getSessionById.value(sessionId);
    if (!session) {
      return;
    }

    if (session.status === SESSION_STATUS.CLAIMED) {
      if (!session.claimedBy.includes(agentId.value)) {
        playSoundOnEvent = false;
        updateReadStatus = false;
      }
    }

    /* TODO: How can we move this feature to WebWorker? */
    if (playSoundOnEvent) {
      const EVENTS = {
        WHISPER: 'WhisperToAgent',
        TRANSCRIPT: 'Transcript',
        ACTION: 'Action',
      };

      /* TODO: Use new sessions store! */
      const { sessionId: activeSessionId } = AppRouter.getQuery();
      const whisper = _.find(events, { event: EVENTS.WHISPER });
      const transcript = _.find(events, { event: EVENTS.TRANSCRIPT });
      const action = _.find(events, { event: EVENTS.ACTION });
      const notify = !document.hasFocus() || sessionId !== activeSessionId;

      if (whisper && whisper.notify && notify) {
        Ringtone.play('notify');
        notifyInBrowser({
          message: whisper.eventValue.message,
          params: { sessionId },
        });
      }

      if (transcript && !action) {
        Ringtone.play('message');
      }
    }

    if (updateReadStatus) {
      args.markAsRead
        ? await saveReadTextEvent({ sessionId, events })
        : addToNewEventsCount({ sessionId, events });
    }

    /* Update sessions order in conversations list */
    const maxTimestamp = _.maxBy(events, e => {
      return new Date(e.timestamp).getTime();
    });

    if (maxTimestamp) {
      updateEventTimestamp({
        sessionId,
        timestamp: maxTimestamp.timestamp,
      });

      updateContactEventTimestamp({
        contactId: session.contactId,
        timestamp: maxTimestamp.timestamp,
      });
    }

    return events;
  };

  return {
    addEventsToStore,
    getAPIEventsBySessionId,
    createUIEvent,
    wsAddEvents,
  };
};

export const useEvents = () => {
  return {
    ...useEventsActions(),
    ...storeToRefs(useEventsStore()),
  };
};
